import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  EventResultDTO,
  SseSessionStartData,
} from '@nora-monorepo/nora-api-client';
import { EventPattern } from '@nora-monorepo/nora-api-client';

import { useConfigStore } from '#ui-shared/stores/config-store';
import { useGenericAppStore } from '#ui-shared/stores/generic-app-store';
import { FetchSseSource } from '#ui-shared/stores/sse/fetch-sse-source';
import type { SseSource } from '#ui-shared/stores/sse/sse-source';
import { WebViewSseSource } from '#ui-shared/stores/sse/webview-sse-source';
import { useWebViewStore } from '#ui-shared/stores/webview/webview-store';
import { convertDates } from '#ui-shared/utils/convert-dates';

export type SseEventHandler = (event: EventResultDTO) => void | Promise<void>;

export const useSseStore = defineStore('sseStore', () => {
  const SSE_SESSION_ID = ref<string>();

  const eventHandlers = new Map<string, Set<SseEventHandler>>();

  let source: SseSource | undefined;

  const webViewStore = useWebViewStore();
  const appStore = useGenericAppStore();
  const configStore = useConfigStore();

  async function startListening(): Promise<void> {
    if (source) {
      return;
    }

    if (webViewStore.isWebView) {
      source = new WebViewSseSource();
    }
    else {
      source = new FetchSseSource(localStorage.getItem('deviceId'));
    }

    const activeSource = source;
    appStore.setStreamConnected(false);
    activeSource.start({
      onmessage: (data) => {
        if (source === activeSource) {
          handleEvent(data);
        }
      },
      onopen: () => {
        // Stream only; device network is owned by the browser/native signal.
        if (source === activeSource) {
          appStore.setStreamConnected(true);
        }
      },
      onerror: () => {
        if (source === activeSource) {
          appStore.setStreamConnected(false);
        }
      },
    });
  }

  function stopListening(): void {
    source?.stop();
    source = undefined;
    appStore.setStreamConnected(false);
  }

  function addEventHandler(type: string, listener: SseEventHandler): () => void {
    const handlers = eventHandlers.get(type);

    if (handlers) {
      handlers.add(listener);
    }
    else {
      eventHandlers.set(type, new Set([listener]));
    }

    const removeHandler = (): void => {
      const existingHandlers = eventHandlers.get(type);

      if (!existingHandlers) {
        return;
      }

      existingHandlers.delete(listener);

      if (existingHandlers.size === 0) {
        eventHandlers.delete(type);
      }
    };

    return removeHandler;
  }

  /**
   * Handle when receiving an SSE-event.
   *
   * Will attempt to parse the incoming data, and pass on the data to the correct handlers based
   * on the event.pattern property. See {@link SseEventHandler}.
   * @param rawData
   */
  async function handleEvent(rawData: string): Promise<void> {
    if (!rawData || rawData.trim().length === 0) {
      return;
    }

    let event: EventResultDTO;

    try {
      event = JSON.parse(rawData) as EventResultDTO;
    }
    catch {
      console.warn('SSE store received invalid JSON payload');
      return;
    }

    if (!event?.pattern) {
      console.warn('SSE store received event without pattern');
      return;
    }

    event = convertDates(event);

    const data: unknown = event.eventData;

    if (configStore.get('general.debug', false)) {
      console.debug(`SSE store received event with pattern ${event.pattern}`, data);
    }

    switch (event.pattern) {
      case EventPattern.SseSessionStart: {
        const sessionStartEvent = data as SseSessionStartData;

        SSE_SESSION_ID.value = sessionStartEvent.sessionId;
        localStorage.setItem('deviceId', sessionStartEvent.deviceId);

        break;
      }

      case EventPattern.Ping: {
        break;
      }

      default: {
        const handlers = eventHandlers.get(event.pattern);

        if (!handlers) {
          console.warn('Unhandled event with pattern ', event.pattern);

          break;
        }

        for (const handler of handlers) {
          void Promise.resolve()
            .then(() => handler(event))
            .catch((error: unknown) => {
              console.error(`SSE handler for pattern ${event.pattern} failed:`, error);
            });
        }

        break;
      }
    }
  }

  return {
    SSE_SESSION_ID,
    startListening,
    stopListening,
    addEventHandler,
  };
});
